"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findRecommendedCardTotal = exports.generatePricingCards = exports.getTierPrice = void 0;
const getTierPrice = (tier) => {
    if (tier == null)
        return 0;
    if (typeof tier === "number")
        return tier;
    return tier.price ?? 0;
};
exports.getTierPrice = getTierPrice;
const resolveRecommendedKey = (cards, preferredCardKey) => {
    if (!cards.length)
        return null;
    if (preferredCardKey === undefined) {
        return cards[0]?.key ?? null;
    }
    return cards.find((card) => card.key === preferredCardKey)?.key ?? null;
};
const buildCard = (key, label, currency, procedures, resolver) => {
    const cardProcedures = procedures
        .map(resolver)
        .filter((entry) => Boolean(entry));
    if (!cardProcedures.length)
        return null;
    const total = cardProcedures.reduce((acc, entry) => acc + entry.amount, 0);
    return {
        key,
        label,
        currency,
        total,
        procedures: cardProcedures,
    };
};
const generatePricingCards = ({ procedures, tieredPricingEnabled, preferredCardKey, }) => {
    if (!procedures.length) {
        return { cards: [], recommendedCardKey: null };
    }
    const currency = procedures[0]?.pricing.currency ?? "USD";
    if (!tieredPricingEnabled) {
        const flatProcedures = procedures.map((procedure) => ({
            lineItemId: procedure.lineItemId,
            procedureId: procedure.procedureId,
            name: procedure.name,
            tier: "flat",
            amount: Number(procedure.pricing.flatAmount ?? 0),
        }));
        const flatTotal = flatProcedures.reduce((acc, entry) => acc + (entry.amount || 0), 0);
        const cards = [
            {
                key: "flat",
                label: null,
                currency,
                total: flatTotal,
                procedures: flatProcedures,
            },
        ];
        const recommendedCardKey = resolveRecommendedKey(cards, preferredCardKey);
        const cardsWithRecommendation = cards.map((card) => ({
            ...card,
            isRecommended: card.key === recommendedCardKey,
        }));
        return {
            cards: cardsWithRecommendation,
            recommendedCardKey,
        };
    }
    const hasGood = procedures.some((procedure) => (0, exports.getTierPrice)(procedure.pricing.tiers?.good) > 0);
    const hasBetter = procedures.some((procedure) => (0, exports.getTierPrice)(procedure.pricing.tiers?.better) > 0);
    const hasBest = procedures.some((procedure) => (0, exports.getTierPrice)(procedure.pricing.tiers?.best) > 0);
    const cards = [];
    if (hasGood) {
        const goodCard = buildCard("good", "Good", currency, procedures, (procedure) => {
            const amount = (0, exports.getTierPrice)(procedure.pricing.tiers?.good);
            if (amount <= 0)
                return null;
            return {
                lineItemId: procedure.lineItemId,
                procedureId: procedure.procedureId,
                name: procedure.name,
                tier: "good",
                amount,
            };
        });
        if (goodCard) {
            cards.push(goodCard);
        }
    }
    if (hasBetter) {
        const betterCard = buildCard("better", "Better", currency, procedures, (procedure) => {
            const betterAmount = (0, exports.getTierPrice)(procedure.pricing.tiers?.better);
            const goodAmount = (0, exports.getTierPrice)(procedure.pricing.tiers?.good);
            if (betterAmount > 0) {
                return {
                    lineItemId: procedure.lineItemId,
                    procedureId: procedure.procedureId,
                    name: procedure.name,
                    tier: "better",
                    amount: betterAmount,
                };
            }
            if (goodAmount > 0) {
                return {
                    lineItemId: procedure.lineItemId,
                    procedureId: procedure.procedureId,
                    name: procedure.name,
                    tier: "good",
                    amount: goodAmount,
                };
            }
            return null;
        });
        if (betterCard) {
            cards.push(betterCard);
        }
    }
    if (hasBest) {
        const bestCard = buildCard("best", "Best", currency, procedures, (procedure) => {
            const bestAmount = (0, exports.getTierPrice)(procedure.pricing.tiers?.best);
            const betterAmount = (0, exports.getTierPrice)(procedure.pricing.tiers?.better);
            const goodAmount = (0, exports.getTierPrice)(procedure.pricing.tiers?.good);
            const resolvedAmount = bestAmount > 0
                ? { tier: "best", amount: bestAmount }
                : betterAmount > 0
                    ? { tier: "better", amount: betterAmount }
                    : goodAmount > 0
                        ? { tier: "good", amount: goodAmount }
                        : null;
            if (!resolvedAmount)
                return null;
            return {
                lineItemId: procedure.lineItemId,
                procedureId: procedure.procedureId,
                name: procedure.name,
                tier: resolvedAmount.tier,
                amount: resolvedAmount.amount,
            };
        });
        if (bestCard) {
            cards.push(bestCard);
        }
    }
    const recommendedCardKey = resolveRecommendedKey(cards, preferredCardKey);
    const cardsWithRecommendation = cards.map((card) => ({
        ...card,
        isRecommended: card.key === recommendedCardKey,
    }));
    return {
        cards: cardsWithRecommendation,
        recommendedCardKey,
    };
};
exports.generatePricingCards = generatePricingCards;
const findRecommendedCardTotal = (cards, recommendedKey) => {
    if (!cards || !cards.length)
        return null;
    const targetKey = recommendedKey ?? cards[0]?.key ?? null;
    if (!targetKey)
        return null;
    const target = cards.find((card) => card.key === targetKey);
    return target ? target.total : null;
};
exports.findRecommendedCardTotal = findRecommendedCardTotal;
//# sourceMappingURL=pricingCards.js.map